/**
 * useWateringStop — "stop everything at this site", and the hold that follows.
 *
 * The stop is not instantaneous and the screen must not pretend otherwise. The
 * pumps go off first and have to CONFIRM before a single valve closes, which is
 * a radio round trip on an ESP-NOW site and can be minutes of retried frames on
 * an XBee one. So `stop()` returns once the request is lodged, and this hook
 * polls until the site reaches a terminal phase.
 *
 * Two things it deliberately does NOT do:
 *
 *  - It does not own the outcome. The sequence runs on the server (or on the
 *    bridge), not here, so closing the tab mid-stop changes nothing — reopening
 *    the page picks the same request back up. A stop driven from the browser
 *    would strand half-closed valves the moment somebody drove out of signal.
 *
 *  - It does not stop polling on `stalled`. That phase means a pump would not
 *    confirm and its valves are being held open on purpose; the page keeps
 *    showing it until a person acts, because it is the one outcome that needs
 *    somebody to walk to the pump.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import espnowService, { type WateringStopStatus } from '../services/espnowIrrigation';
import { errorMessage } from '../utils/apiResponse';

// While a stop is in flight. Fast enough that "Pumps confirmed off" appears
// while somebody is still looking at the screen, slow enough not to hammer a
// farm on a metered link.
const IN_FLIGHT_POLL_MS = 3_000;

// A stop in flight has not finished yet; everything else has an answer.
const IN_FLIGHT: ReadonlyArray<string> = ['pumps', 'valves', 'delegated'];

export interface UseWateringStopResult {
  status: WateringStopStatus | null;
  /** True between pressing Stop and the site reaching a terminal phase. */
  inFlight: boolean;
  /** True while a stop or resume request is on the wire. */
  busy: boolean;
  error: string | null;
  stop: (reason?: string) => Promise<void>;
  resume: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function isStopInFlight(status: WateringStopStatus | null): boolean {
  const req = status?.request;
  if (!req || req.finished_at) return false;
  return IN_FLIGHT.includes(req.phase);
}

/**
 * `enabled: false` skips the initial read entirely, for callers that already
 * know the hold state from a payload they have (the farm overview carries it)
 * and only need the actions. Without it, a page with eight controller cards
 * would fire eight status requests on every mount to learn what it was already
 * told.
 */
export function useWateringStop(
  controllerId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {}
): UseWateringStopResult {
  const [status, setStatus] = useState<WateringStopStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!controllerId) return;
    try {
      const next = await espnowService.getStopStatus(controllerId);
      if (alive.current) setStatus(next);
    } catch (err) {
      // A failed status read is not worth an error banner of its own: the
      // previous answer is still on screen and the next poll is three seconds
      // away. A failed stop or resume IS, and those set `error` below.
      if (import.meta.env.DEV) console.debug('stop-status poll failed', err);
    }
  }, [controllerId]);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
  }, [enabled, refresh]);

  const inFlight = isStopInFlight(status);

  useEffect(() => {
    if (!inFlight || !controllerId) return;
    const timer = window.setInterval(() => void refresh(), IN_FLIGHT_POLL_MS);
    return () => window.clearInterval(timer);
  }, [inFlight, controllerId, refresh]);

  const stop = useCallback(async (reason?: string) => {
    if (!controllerId) return;
    setBusy(true);
    setError(null);
    try {
      const next = await espnowService.stopWatering(controllerId, reason);
      if (alive.current) setStatus(next);
    } catch (err) {
      if (alive.current) setError(errorMessage(err, 'Could not stop watering at this site.'));
      throw err;
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [controllerId]);

  const resume = useCallback(async () => {
    if (!controllerId) return;
    setBusy(true);
    setError(null);
    try {
      const next = await espnowService.resumeWatering(controllerId);
      if (alive.current) setStatus(next);
    } catch (err) {
      if (alive.current) setError(errorMessage(err, 'Could not resume watering at this site.'));
      throw err;
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [controllerId]);

  return { status, inFlight, busy, error, stop, resume, refresh };
}

export default useWateringStop;
